import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin } from 'lucide-react';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <footer className="relative z-50 py-16 px-4 sm:px-6 lg:px-8 border-t-2 border-brand">
      {/* Solid background to cover the canvas */}
      <div className="absolute inset-0 bg-[#1e1230]" style={{ zIndex: -1 }}></div>
      <div className="max-w-7xl mx-auto relative">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <a 
              href="/" 
              onClick={handleLogoClick}
              className="flex items-center space-x-3 mb-6 cursor-pointer hover:opacity-80 transition-opacity"
            >
              <div className="w-14 h-14 rounded-xl overflow-hidden shadow-lg shadow-brand/30">
                <img 
                  src="/logo.png" 
                  alt="RANSEN" 
                  className="w-full h-full object-contain logo-motion"
                />
              </div>
              <div>
                <div className="text-2xl font-heading font-bold text-white">RANSEN</div>
                <div className="text-sm text-brand font-medium">AI-Driven Growth Agency</div>
              </div>
            </a>
            <p className="text-white/90 font-body max-w-md mb-6 leading-relaxed">
              Full-service digital marketing agency helping businesses across Canada grow with AI-powered strategies, 
              data-driven campaigns, and cutting-edge marketing technology.
            </p>
            <div className="space-y-4">
              <a href="mailto:marketing@ransen.ca" className="flex items-center text-white/90 hover:text-brand transition-colors font-medium">
                <Mail size={20} className="mr-3 text-brand" />
                marketing@ransen.ca
              </a>
              <div className="flex items-center text-white/90 font-medium">
                <MapPin size={20} className="mr-3 text-brand" />
                Toronto, Canada
              </div>
            </div>
            
            {/* Social Links */}
            <div className="flex items-center gap-4 mt-6">
              <a 
                href="https://www.instagram.com/ransen_marketing/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-brand/20 border border-white/20 hover:border-brand/50 flex items-center justify-center transition-all hover:scale-110"
                aria-label="Follow us on Instagram"
              >
                <Instagram size={20} className="text-white/80 hover:text-brand" />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61587451496930" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-brand/20 border border-white/20 hover:border-brand/50 flex items-center justify-center transition-all hover:scale-110"
                aria-label="Follow us on Facebook"
              >
                <Facebook size={20} className="text-white/80 hover:text-brand" />
              </a>
              <a 
                href="https://www.linkedin.com/company/ransen/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-brand/20 border border-white/20 hover:border-brand/50 flex items-center justify-center transition-all hover:scale-110"
                aria-label="Follow us on LinkedIn"
              >
                <Linkedin size={20} className="text-white/80 hover:text-brand" />
              </a>
            </div>
          </div>
          
          {/* Services Column */}
          <div>
            <h4 className="font-heading font-bold text-white mb-5 text-lg">Services</h4>
            <ul className="space-y-3">
              <li><a href="/services/paid-media-advertising" className="text-white/80 hover:text-brand transition-colors">Paid Media & PPC</a></li>
              <li><a href="/services/search-engine-optimization" className="text-white/80 hover:text-brand transition-colors">SEO Services</a></li>
              <li><a href="/services/social-media-management" className="text-white/80 hover:text-brand transition-colors">Social Media Marketing</a></li>
              <li><a href="/services/website-design-development" className="text-white/80 hover:text-brand transition-colors">Web Design</a></li>
              <li><a href="/services/video-production" className="text-white/80 hover:text-brand transition-colors">Video Production</a></li>
              <li><a href="/services/ai-automation" className="text-white/80 hover:text-brand transition-colors">AI Automation</a></li>
              <li><a href="/services/llmo-optimization" className="text-white/80 hover:text-brand transition-colors">LLMO</a></li>
              <li><a href="/services/aeo-optimization" className="text-white/80 hover:text-brand transition-colors">AEO</a></li>
            </ul>
          </div>
          
          {/* Industries Column */}
          <div>
            <h4 className="font-heading font-bold text-white mb-5 text-lg">Industries</h4>
            <ul className="space-y-3">
              <li><a href="/industries/ecommerce-retail-marketing" className="text-white/80 hover:text-brand transition-colors">E-Commerce</a></li>
              <li><a href="/industries/saas-technology-marketing" className="text-white/80 hover:text-brand transition-colors">SaaS & Tech</a></li>
              <li><a href="/industries/healthcare-wellness-marketing" className="text-white/80 hover:text-brand transition-colors">Healthcare</a></li>
              <li><a href="/industries/professional-services-marketing" className="text-white/80 hover:text-brand transition-colors">Professional Services</a></li>
              <li><a href="/industries/real-estate-marketing" className="text-white/80 hover:text-brand transition-colors">Real Estate</a></li>
              <li><a href="/industries/finance-insurance-marketing" className="text-white/80 hover:text-brand transition-colors">Finance</a></li>
            </ul>
          </div>
          
          {/* Company Column */}
          <div>
            <h4 className="font-heading font-bold text-white mb-5 text-lg">Company</h4>
            <ul className="space-y-3">
              <li><a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} className="text-white/80 hover:text-brand transition-colors">About Us</a></li>
              <li><a href="/blog" className="text-white/80 hover:text-brand transition-colors">Blog</a></li>
              <li><a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="text-white/80 hover:text-brand transition-colors">Contact</a></li>
            </ul>
            
            <h4 className="font-heading font-bold text-white mb-5 mt-8 text-lg">Legal</h4>
            <ul className="space-y-3">
              <li><a href="/privacy-policy" className="text-white/80 hover:text-brand transition-colors">Privacy Policy</a></li>
              <li><a href="/terms-of-service" className="text-white/80 hover:text-brand transition-colors">Terms of Service</a></li>
              <li><a href="/cookie-policy" className="text-white/80 hover:text-brand transition-colors">Cookie Policy</a></li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-white/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/70 font-body text-sm">
              © 2026 RANSEN Digital Marketing Agency. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-white/70 text-sm hidden sm:block">Serving clients across Canada and worldwide</span>
              <div className="flex items-center gap-3">
                <a 
                  href="https://www.instagram.com/ransen_marketing/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white/50 hover:text-brand transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram size={18} />
                </a>
                <a 
                  href="https://www.facebook.com/profile.php?id=61587451496930" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white/50 hover:text-brand transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook size={18} />
                </a>
                <a 
                  href="https://www.linkedin.com/company/ransen/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white/50 hover:text-brand transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
